import { Link } from "react-router";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@nosible/shared/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@nosible/shared/components/ui/table";
import { Badge } from "@nosible/shared/components/ui/badge";
import { Button } from "@nosible/shared/components/ui/button";
import { trpc } from "@/admin/lib/trpc";
import { CreditCard, Building2, User, Plus } from "lucide-react";
import type { AppRouter } from "@nosible/api";
import type { inferRouterOutputs } from "@trpc/server";

const statusBadgeClasses: Record<string, string> = {
  active: "bg-neon-green text-obsidian",
  trialing: "bg-signal-blue text-ghost-white",
  canceled: "bg-red-500/15 text-red-400",
  past_due: "bg-red-500/15 text-red-400",
};

type RouterOutputs = inferRouterOutputs<AppRouter>;

type OwnerSubscription =
  RouterOutputs["admin"]["getSubscriptionsForOwner"][number];

type OwnerSubscriptionsProps = {
  ownerId: string;
  ownerName?: string | null;
};

const formatDate = (value?: Date | string | null) =>
  value ? new Date(value).toLocaleDateString() : "Indefinite";

const OwnerTypeIcon = ({ ownerType }: { ownerType?: string | null }) => {
  if (ownerType === "organization") {
    return <Building2 className="h-4 w-4 text-violet-pulse" />;
  }

  return <User className="h-4 w-4 text-signal-blue" />;
};

export function OwnerSubscriptions({ ownerId, ownerName }: OwnerSubscriptionsProps) {
  const {
    data: subscriptions,
    isLoading,
    error,
  } = trpc.admin.getSubscriptionsForOwner.useQuery(
    { ownerId },
    { enabled: !!ownerId }
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Subscriptions
            </CardTitle>
            <CardDescription>
              {ownerName ? `Subscriptions for ${ownerName}` : "All subscriptions for this owner"}
            </CardDescription>
          </div>
          <Button asChild size="sm" className="bg-neon-green text-obsidian hover:bg-neon-green/90">
            <Link to={`/subscriptions/create?ownerId=${ownerId}&ownerType=user`}>
              <Plus className="h-4 w-4 mr-1" />
              Create Subscription
            </Link>
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="flex items-center justify-center py-8 text-chromium-mist">
            Loading subscriptions...
          </div>
        )}
        {error && (
          <div className="flex items-center justify-center py-8 text-red-400">
            {error.message}
          </div>
        )}
        {!isLoading && !error && subscriptions?.length === 0 && (
          <div className="text-center py-6 text-chromium-mist">
            <CreditCard className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No subscriptions</p>
          </div>
        )}
        {!isLoading && !error && subscriptions && subscriptions.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Usage</TableHead>
                <TableHead>Period End</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {subscriptions.map((sub: OwnerSubscription) => (
                <TableRow key={sub.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {sub.name}
                    </div>
                  </TableCell>
                  <TableCell className="font-medium text-silica">
                    <Link
                      to={`/plans/${sub.planId}`}
                      className="hover:text-neon-green transition-colors"
                    >
                      {sub.planId}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={sub.status === "active" ? "default" : "secondary"}
                      className={statusBadgeClasses[sub.status] || ""}
                    >
                      {sub.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{sub.totalCount ?? 0}</TableCell>
                  <TableCell className="text-chromium-mist">
                    {formatDate(sub.periodEnd)}
                  </TableCell>
                  <TableCell className="text-chromium-mist">
                    {formatDate(sub.createdAt)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" asChild>
                        <Link to={`/subscriptions/${sub.id}`}>View</Link>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
